import { useEffect, useState } from "react";
import { Loader2, CalendarCheck } from "lucide-react";
import { myservicesBookings } from "@/lib/api";

/**
 * Bookings rendered in the same card layout as the CRM results — one card per
 * booking (avatar + name + service/email, with date/time/status on the right).
 * Used inside the CRM (dealer + admin) so all "list of contacts" views match.
 */
export default function BookingsList({ rec }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    myservicesBookings(rec)
      .then((r) => alive && setRows(r))
      .catch(() => alive && setRows([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [rec]);

  if (loading)
    return <div className="py-16 text-center text-zinc-400" data-testid="bookings-loading"><Loader2 className="animate-spin inline" size={20} /></div>;
  if (rows.length === 0)
    return (
      <div className="rounded-2xl border border-dashed border-zinc-300 bg-white py-16 text-center text-zinc-500" data-testid="bookings-empty">
        No bookings yet.
      </div>
    );

  return (
    <div className="space-y-3" data-testid="bookings-list">
      {rows.map((b) => {
        const d = b.datetime ? new Date(b.datetime) : null;
        return (
          <div
            key={b.id}
            className="flex items-center gap-3 rounded-2xl border border-zinc-200 bg-white shadow-sm px-4 py-3.5"
            data-testid={`booking-${b.id}`}
          >
            <div className="h-11 w-11 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold shrink-0">
              {(b.name || "?").charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-zinc-900 truncate">{b.name}</div>
              <div className="text-xs text-zinc-500 truncate">
                {b.service}{b.email ? ` · ${b.email}` : ""}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-xs font-semibold text-zinc-700 inline-flex items-center gap-1 justify-end">
                <CalendarCheck size={12} className="text-indigo-500" />
                {d ? d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—"}
              </div>
              <div className="text-[11px] text-zinc-400 mt-0.5">
                {d ? d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : ""}
                {b.status ? ` · ${b.status}` : ""}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
